/**
 * GPS Utility Functions for Movement App
 *
 * Core algorithms for distance calculation, elevation gain,
 * pace calculation, and auto-pause detection.
 */

// ──────────────────────────────────────────────────────────────────────────────
// TYPES
// ──────────────────────────────────────────────────────────────────────────────

export interface GPSPoint {
  sampleId?: string;
  segmentId?: number;
  latitude: number;
  longitude: number;
  altitude?: number;
  accuracy?: number;
  timestamp: string;
  speed?: number; // meters per second
  heading?: number;
}

export interface CalculatedStats {
  distanceMeters: number;
  durationSeconds: number;
  movingTimeSeconds: number;
  elevationGainMeters: number;
  elevationLossMeters: number;
  avgPaceSecondsPerKm: number;
  avgSpeedMps: number;
  maxSpeedMps: number;
  recordingGapSeconds: number;
  maxRecordingGapSeconds: number;
}

export type ActivityQualityFlag =
  | "recording-gap"
  | "average-speed-exceeds-maximum"
  | "activity-speed-impossible";

export type ActivityStatsOptions = {
  startedAt?: string | number | Date | null;
  completedAt?: string | number | Date | null;
  recordingGapThresholdSeconds?: number;
};

type GPSActivityType = "run" | "ride" | "hike" | "walk";

export type GPSPointRejectionReason =
  | "coordinates-invalid"
  | "timestamp-invalid"
  | "timestamp-before-activity-start"
  | "timestamp-non-monotonic"
  | "accuracy-unusable"
  | "reported-speed-impossible"
  | "segment-distance-invalid"
  | "segment-within-accuracy-drift"
  | "derived-speed-impossible"
  | "rapid-fix-lower-quality"
  | "rapid-fix-superseded"
  | "large-jump-without-accuracy-overlap";

export type GPSQualityRules = {
  maxAccuracyMeters: number;
  maxSegmentSpeedMps: number;
  largeJumpMeters: number;
  accuracyOverlapGraceMeters: number;
  driftGraceMeters: number;
  minMotionSpeedMps: number;
  minDeltaSeconds: number;
  startStaleGraceMs: number;
};

export type GPSPointRejection = {
  index: number;
  reason: GPSPointRejectionReason;
  point: GPSPoint;
};

export const GPS_ACTIVITY_QUALITY_RULES: Record<
  GPSActivityType,
  GPSQualityRules
> = {
  run: {
    maxAccuracyMeters: 35,
    maxSegmentSpeedMps: 9,
    largeJumpMeters: 100,
    accuracyOverlapGraceMeters: 15,
    driftGraceMeters: 15,
    minMotionSpeedMps: 0.8,
    minDeltaSeconds: 0.75,
    startStaleGraceMs: 3000,
  },
  walk: {
    maxAccuracyMeters: 35,
    maxSegmentSpeedMps: 3.5,
    largeJumpMeters: 80,
    accuracyOverlapGraceMeters: 15,
    driftGraceMeters: 12,
    minMotionSpeedMps: 0.35,
    minDeltaSeconds: 0.75,
    startStaleGraceMs: 3000,
  },
  hike: {
    maxAccuracyMeters: 60,
    maxSegmentSpeedMps: 6,
    largeJumpMeters: 100,
    accuracyOverlapGraceMeters: 20,
    driftGraceMeters: 18,
    minMotionSpeedMps: 0.35,
    minDeltaSeconds: 0.75,
    startStaleGraceMs: 3000,
  },
  ride: {
    maxAccuracyMeters: 35,
    maxSegmentSpeedMps: 25,
    largeJumpMeters: 250,
    accuracyOverlapGraceMeters: 20,
    driftGraceMeters: 18,
    minMotionSpeedMps: 1.5,
    minDeltaSeconds: 0.75,
    startStaleGraceMs: 3000,
  },
};

/**
 * GPS rules for a recording whose type is decided at save ("Start tracking"):
 * foot-level motion and drift thresholds so slow walking is kept, up to a
 * ride's speed so a bike ride is too. The saved type re-filters the route on
 * the server. Mirrored natively in HiiveGPSQuality.swift and
 * HiiveMovementGpsAccumulator.java.
 */
export const GPS_AUTO_QUALITY_RULES: GPSQualityRules = {
  maxAccuracyMeters: 60,
  maxSegmentSpeedMps: 25,
  largeJumpMeters: 250,
  accuracyOverlapGraceMeters: 20,
  driftGraceMeters: 18,
  minMotionSpeedMps: 0.35,
  minDeltaSeconds: 0.75,
  startStaleGraceMs: 3000,
};

export const GPS_AUTO_PROFILE = "auto";

export function getGPSQualityRules(
  activityType: string | null | undefined,
): GPSQualityRules {
  if (activityType === GPS_AUTO_PROFILE) return GPS_AUTO_QUALITY_RULES;
  if (
    activityType === "run" ||
    activityType === "ride" ||
    activityType === "hike" ||
    activityType === "walk"
  ) {
    return GPS_ACTIVITY_QUALITY_RULES[activityType];
  }

  return GPS_ACTIVITY_QUALITY_RULES.run;
}

// ──────────────────────────────────────────────────────────────────────────────
// DISTANCE CALCULATION (Haversine Formula)
// ──────────────────────────────────────────────────────────────────────────────

const EARTH_RADIUS_METERS = 6371000;

/**
 * Calculate distance between two coordinates using Haversine formula
 * @returns Distance in meters
 */
export function haversineDistance(
  lat1: number,
  lon1: number,
  lat2: number,
  lon2: number,
): number {
  const dLat = toRadians(lat2 - lat1);
  const dLon = toRadians(lon2 - lon1);

  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(toRadians(lat1)) *
      Math.cos(toRadians(lat2)) *
      Math.sin(dLon / 2) *
      Math.sin(dLon / 2);

  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return EARTH_RADIUS_METERS * c;
}

function toRadians(degrees: number): number {
  return degrees * (Math.PI / 180);
}

/**
 * Calculate total distance of a GPS track
 * @param points Array of GPS points
 * @returns Total distance in meters
 */
export function splitGPSPointSegments<T extends { segmentId?: number | null }>(
  points: T[],
): T[][] {
  const segments: T[][] = [];
  for (const point of points) {
    const last = segments.at(-1);
    if (!last || (last[0].segmentId ?? 0) !== (point.segmentId ?? 0))
      segments.push([point]);
    else last.push(point);
  }
  return segments;
}

export function calculateTotalDistance(points: GPSPoint[]): number {
  if (points.length < 2) return 0;

  let totalDistance = 0;
  for (let i = 1; i < points.length; i++) {
    if ((points[i - 1].segmentId ?? 0) !== (points[i].segmentId ?? 0)) continue;
    totalDistance += haversineDistance(
      points[i - 1].latitude,
      points[i - 1].longitude,
      points[i].latitude,
      points[i].longitude,
    );
  }

  return totalDistance;
}

/**
 * Filter out GPS points with poor accuracy
 * @param points Array of GPS points
 * @param maxAccuracy Maximum acceptable accuracy in meters (default: 20)
 */
export function filterByAccuracy(
  points: GPSPoint[],
  maxAccuracy = 20,
): GPSPoint[] {
  return points.filter((p) => !p.accuracy || p.accuracy <= maxAccuracy);
}

// ──────────────────────────────────────────────────────────────────────────────
// ELEVATION CALCULATION
// ──────────────────────────────────────────────────────────────────────────────

/**
 * Calculate elevation gain from GPS track
 * Uses a threshold to filter out GPS noise (default: 3 meters)
 * @returns Elevation gain in meters
 */
export function calculateElevationGain(
  points: GPSPoint[],
  threshold = 3,
): number {
  const segments = splitGPSPointSegments(points);
  if (segments.length > 1)
    return segments.reduce(
      (sum, segment) => sum + calculateElevationGain(segment),
      0,
    );
  const altitudeProfile = buildDistanceBucketedAltitudeProfile(points);
  if (altitudeProfile.length < 2) return 0;

  let totalGain = 0;
  let lastValidAltitude = altitudeProfile[0];

  for (let i = 1; i < altitudeProfile.length; i++) {
    const currentAltitude = altitudeProfile[i];
    const diff = currentAltitude - lastValidAltitude;

    // Only count if above threshold (reduces noise)
    if (diff > threshold) {
      totalGain += diff;
      lastValidAltitude = currentAltitude;
    } else if (diff < -threshold) {
      // Update reference for descents too
      lastValidAltitude = currentAltitude;
    }
  }

  return totalGain;
}

/**
 * Calculate elevation loss from GPS track
 */
export function calculateElevationLoss(
  points: GPSPoint[],
  threshold = 3,
): number {
  const segments = splitGPSPointSegments(points);
  if (segments.length > 1)
    return segments.reduce(
      (sum, segment) => sum + calculateElevationLoss(segment),
      0,
    );
  const altitudeProfile = buildDistanceBucketedAltitudeProfile(points);
  if (altitudeProfile.length < 2) return 0;

  let totalLoss = 0;
  let lastValidAltitude = altitudeProfile[0];

  for (let i = 1; i < altitudeProfile.length; i++) {
    const currentAltitude = altitudeProfile[i];
    const diff = currentAltitude - lastValidAltitude;

    if (diff < -threshold) {
      totalLoss += Math.abs(diff);
      lastValidAltitude = currentAltitude;
    } else if (diff > threshold) {
      lastValidAltitude = currentAltitude;
    }
  }

  return totalLoss;
}

const ELEVATION_BUCKET_DISTANCE_METERS = 15;

function median(values: number[]): number {
  const sorted = [...values].sort((a, b) => a - b);
  const middle = Math.floor(sorted.length / 2);
  if (sorted.length % 2 === 0) {
    return (sorted[middle - 1] + sorted[middle]) / 2;
  }
  return sorted[middle];
}

/**
 * GPS altitude is much noisier than horizontal position. Sampling one median
 * altitude per distance bucket prevents two interleaved altitude planes from
 * being accumulated as a climb on every fix while preserving real hills.
 */
function buildDistanceBucketedAltitudeProfile(points: GPSPoint[]): number[] {
  if (points.length === 0) return [];

  const profile: number[] = [];
  let bucketAltitudes: number[] = [];
  let bucketDistanceMeters = 0;

  for (let index = 0; index < points.length; index += 1) {
    const point = points[index];
    if (typeof point.altitude === "number" && Number.isFinite(point.altitude)) {
      bucketAltitudes.push(point.altitude);
    }

    if (index > 0) {
      const previous = points[index - 1];
      const segmentDistance = haversineDistance(
        previous.latitude,
        previous.longitude,
        point.latitude,
        point.longitude,
      );
      if (Number.isFinite(segmentDistance) && segmentDistance > 0) {
        bucketDistanceMeters += segmentDistance;
      }
    }

    if (
      bucketDistanceMeters >= ELEVATION_BUCKET_DISTANCE_METERS &&
      bucketAltitudes.length > 0
    ) {
      if (profile.length === 0 && bucketAltitudes.length > 1) {
        profile.push(bucketAltitudes[0]);
        profile.push(median(bucketAltitudes.slice(1)));
      } else {
        profile.push(median(bucketAltitudes));
      }
      bucketAltitudes =
        typeof point.altitude === "number" && Number.isFinite(point.altitude)
          ? [point.altitude]
          : [];
      bucketDistanceMeters = 0;
    }
  }

  if (bucketAltitudes.length > 0) {
    if (profile.length === 0) {
      profile.push(median(bucketAltitudes));
    } else if (bucketDistanceMeters > 0) {
      profile.push(median(bucketAltitudes));
    }
  }

  return profile;
}

// ──────────────────────────────────────────────────────────────────────────────
// PACE & SPEED CALCULATION
// ──────────────────────────────────────────────────────────────────────────────

const MIN_PACE_DISTANCE_METERS = 25;
const MAX_DISPLAY_PACE_SECONDS_PER_KM = 60 * 60;

/**
 * Calculate pace in seconds per kilometer
 */
export function calculatePace(
  distanceMeters: number,
  durationSeconds: number,
): number {
  if (distanceMeters < MIN_PACE_DISTANCE_METERS || durationSeconds <= 0)
    return 0;
  return (durationSeconds / distanceMeters) * 1000;
}

/**
 * Calculate speed in meters per second
 */
export function calculateSpeed(
  distanceMeters: number,
  durationSeconds: number,
): number {
  if (durationSeconds === 0) return 0;
  return distanceMeters / durationSeconds;
}

/**
 * Format pace as MM:SS per km/mile
 */
export function formatPace(secondsPerKm: number): string {
  if (
    secondsPerKm <= 0 ||
    secondsPerKm > MAX_DISPLAY_PACE_SECONDS_PER_KM ||
    !Number.isFinite(secondsPerKm)
  )
    return "--:--";
  const rounded = Math.round(secondsPerKm);
  const minutes = Math.floor(rounded / 60);
  const seconds = rounded % 60;
  return `${minutes}:${seconds.toString().padStart(2, "0")}`;
}

/**
 * Format speed as km/h or mph
 */
export function formatSpeed(mps: number, useMetric = true): string {
  if (mps === 0 || !Number.isFinite(mps)) return "0.0";
  const kmh = mps * 3.6;
  if (useMetric) {
    return kmh.toFixed(1);
  }
  return (kmh * 0.621371).toFixed(1);
}

// ──────────────────────────────────────────────────────────────────────────────
// AUTO-PAUSE DETECTION
// ──────────────────────────────────────────────────────────────────────────────

export type AutoPauseDecision = "none" | "pause" | "resume";
export type AutoPauseStatus = "moving" | "paused";

export type AutoPauseDetectorConfig = {
  maxAccuracyMeters: number;
  warmupMs: number;
  pauseThresholdMps: number;
  resumeThresholdMps: number;
  pauseAfterMs: number;
  resumeAfterMs: number;
  minSamples: number;
  maxSpeedMps: number;
  minDeltaSeconds: number;
};

export type AutoPauseDetectorState = {
  status: AutoPauseStatus;
  samples: number;
  belowThresholdSinceMs: number | null;
  aboveResumeSinceMs: number | null;
  lastTimestampMs: number | null;
  lastSpeedMps: number | null;
};

export const DEFAULT_AUTO_PAUSE_CONFIG: Omit<
  AutoPauseDetectorConfig,
  "pauseThresholdMps"
> = {
  maxAccuracyMeters: 35,
  warmupMs: 15_000,
  resumeThresholdMps: 1.0,
  pauseAfterMs: 10_000,
  resumeAfterMs: 2_000,
  minSamples: 5,
  maxSpeedMps: 25,
  minDeltaSeconds: 0.75,
};

export function createAutoPauseDetectorState(): AutoPauseDetectorState {
  return {
    status: "moving",
    samples: 0,
    belowThresholdSinceMs: null,
    aboveResumeSinceMs: null,
    lastTimestampMs: null,
    lastSpeedMps: null,
  };
}

export function isAccuracyUsable(
  accuracyMeters: number | null | undefined,
  maxAccuracyMeters: number,
): boolean {
  if (accuracyMeters == null) return true;
  if (!Number.isFinite(accuracyMeters)) return false;
  return accuracyMeters <= maxAccuracyMeters;
}

function isCoordinateUsable(point: GPSPoint): boolean {
  return (
    Number.isFinite(point.latitude) &&
    Number.isFinite(point.longitude) &&
    point.latitude >= -90 &&
    point.latitude <= 90 &&
    point.longitude >= -180 &&
    point.longitude <= 180
  );
}

function toTimestampMs(value: string): number | null {
  const ms = new Date(value).getTime();
  return Number.isFinite(ms) ? ms : null;
}

export function getGPSPointRejection(params: {
  point: GPSPoint;
  previousTrustedPoint: GPSPoint | null;
  activityType?: string | null;
  activityStartedAtMs?: number | null;
}): GPSPointRejectionReason | null {
  const {
    point,
    previousTrustedPoint,
    activityType,
    activityStartedAtMs = null,
  } = params;
  const rules = getGPSQualityRules(activityType);

  if (!isCoordinateUsable(point)) {
    return "coordinates-invalid";
  }

  const currentTimestampMs = toTimestampMs(point.timestamp);
  if (currentTimestampMs == null) {
    return "timestamp-invalid";
  }

  if (
    activityStartedAtMs != null &&
    Number.isFinite(activityStartedAtMs) &&
    currentTimestampMs < activityStartedAtMs - rules.startStaleGraceMs
  ) {
    return "timestamp-before-activity-start";
  }

  if (!isAccuracyUsable(point.accuracy ?? null, rules.maxAccuracyMeters)) {
    return "accuracy-unusable";
  }

  if (
    point.speed != null &&
    Number.isFinite(point.speed) &&
    (point.speed < 0 || point.speed > rules.maxSegmentSpeedMps)
  ) {
    return "reported-speed-impossible";
  }

  if (!previousTrustedPoint) return null;

  const previousTimestampMs = toTimestampMs(previousTrustedPoint.timestamp);
  if (previousTimestampMs != null) {
    const deltaSeconds = (currentTimestampMs - previousTimestampMs) / 1000;
    if (deltaSeconds <= 0) return "timestamp-non-monotonic";
  }

  const distanceMeters = haversineDistance(
    previousTrustedPoint.latitude,
    previousTrustedPoint.longitude,
    point.latitude,
    point.longitude,
  );

  if (!Number.isFinite(distanceMeters) || distanceMeters < 0) {
    return "segment-distance-invalid";
  }

  if (previousTimestampMs != null) {
    const deltaSeconds = (currentTimestampMs - previousTimestampMs) / 1000;
    const derivedSpeedMps = distanceMeters / deltaSeconds;
    if (
      Number.isFinite(derivedSpeedMps) &&
      derivedSpeedMps > rules.maxSegmentSpeedMps
    ) {
      return "derived-speed-impossible";
    }
  }

  if (distanceMeters <= 2) {
    return null;
  }

  const previousAccuracy = previousTrustedPoint.accuracy;
  const currentAccuracy = point.accuracy;
  const hasAccuracyPair =
    previousAccuracy != null &&
    currentAccuracy != null &&
    Number.isFinite(previousAccuracy) &&
    Number.isFinite(currentAccuracy);
  const reportedSpeed =
    point.speed != null && Number.isFinite(point.speed) ? point.speed : null;

  if (hasAccuracyPair) {
    const plausibleDriftMeters =
      previousAccuracy + currentAccuracy + rules.driftGraceMeters;
    const hasClearMotionSignal =
      reportedSpeed != null && reportedSpeed >= rules.minMotionSpeedMps;

    if (distanceMeters <= plausibleDriftMeters && !hasClearMotionSignal) {
      return "segment-within-accuracy-drift";
    }
  }

  if (distanceMeters >= rules.largeJumpMeters) {
    const plausibleRadiusMeters = hasAccuracyPair
      ? previousAccuracy + currentAccuracy + rules.accuracyOverlapGraceMeters
      : 0;

    if (!hasAccuracyPair || distanceMeters > plausibleRadiusMeters) {
      return "large-jump-without-accuracy-overlap";
    }
  }

  return null;
}

type AppendReliableGPSPointResult = {
  points: GPSPoint[];
  rejected: Array<{
    point: GPSPoint;
    reason: GPSPointRejectionReason;
  }>;
  accepted: boolean;
};

function accuracyScore(point: GPSPoint): number {
  return typeof point.accuracy === "number" && Number.isFinite(point.accuracy)
    ? point.accuracy
    : Number.POSITIVE_INFINITY;
}

type ReliableGPSPointDecision = {
  op: "append" | "replace-last" | "reject";
  rejected: Array<{
    point: GPSPoint;
    reason: GPSPointRejectionReason;
  }>;
};

/**
 * Decides how a new fix joins an already reliable stream. When two fixes
 * arrive inside the activity's minimum time delta, keep the more accurate fix
 * and validate it against the point before the rapid pair. This collapses
 * Android's interleaved provider streams without trusting the chip-reported
 * speed of the noisy fix.
 */
function evaluateReliableGPSPointAppend(
  previousPoint: GPSPoint | null,
  pointBeforePrevious: GPSPoint | null,
  point: GPSPoint,
  activityType?: string | null,
  options?: { activityStartedAtMs?: number | null },
): ReliableGPSPointDecision {
  const standaloneRejection = getGPSPointRejection({
    point,
    previousTrustedPoint: null,
    activityType,
    activityStartedAtMs: options?.activityStartedAtMs ?? null,
  });

  if (standaloneRejection) {
    return { op: "reject", rejected: [{ point, reason: standaloneRejection }] };
  }

  if (
    previousPoint &&
    (previousPoint.segmentId ?? 0) !== (point.segmentId ?? 0)
  ) {
    if (Date.parse(point.timestamp) <= Date.parse(previousPoint.timestamp))
      return {
        op: "reject",
        rejected: [{ point, reason: "timestamp-non-monotonic" }],
      };
    return { op: "append", rejected: [] };
  }
  if (previousPoint) {
    const currentTimestampMs = toTimestampMs(point.timestamp);
    const previousTimestampMs = toTimestampMs(previousPoint.timestamp);
    const rules = getGPSQualityRules(activityType);
    const deltaSeconds =
      currentTimestampMs != null && previousTimestampMs != null
        ? (currentTimestampMs - previousTimestampMs) / 1000
        : null;

    if (
      deltaSeconds != null &&
      deltaSeconds > 0 &&
      deltaSeconds < rules.minDeltaSeconds
    ) {
      if (accuracyScore(point) < accuracyScore(previousPoint)) {
        const replacementRejection = getGPSPointRejection({
          point,
          previousTrustedPoint: pointBeforePrevious,
          activityType,
          activityStartedAtMs: options?.activityStartedAtMs ?? null,
        });

        if (!replacementRejection) {
          return {
            op: "replace-last",
            rejected: [
              { point: previousPoint, reason: "rapid-fix-superseded" },
            ],
          };
        }

        return {
          op: "reject",
          rejected: [{ point, reason: replacementRejection }],
        };
      }

      const rapidRejection = getGPSPointRejection({
        point,
        previousTrustedPoint: previousPoint,
        activityType,
        activityStartedAtMs: options?.activityStartedAtMs ?? null,
      });
      return {
        op: "reject",
        rejected: [
          { point, reason: rapidRejection ?? "rapid-fix-lower-quality" },
        ],
      };
    }
  }

  const rejection = getGPSPointRejection({
    point,
    previousTrustedPoint: previousPoint,
    activityType,
    activityStartedAtMs: options?.activityStartedAtMs ?? null,
  });
  if (rejection) {
    return { op: "reject", rejected: [{ point, reason: rejection }] };
  }

  return { op: "append", rejected: [] };
}

/**
 * Appends a point to an already reliable stream, returning the next stream.
 * See evaluateReliableGPSPointAppend for the rapid-pair collapse semantics.
 */
export function appendReliableGPSPoint(
  reliablePoints: GPSPoint[],
  point: GPSPoint,
  activityType?: string | null,
  options?: { activityStartedAtMs?: number | null },
): AppendReliableGPSPointResult {
  const decision = evaluateReliableGPSPointAppend(
    reliablePoints.at(-1) ?? null,
    reliablePoints.at(-2) ?? null,
    point,
    activityType,
    options,
  );

  if (decision.op === "append") {
    return {
      points: [...reliablePoints, point],
      rejected: decision.rejected,
      accepted: true,
    };
  }

  if (decision.op === "replace-last") {
    return {
      points: [...reliablePoints.slice(0, -1), point],
      rejected: decision.rejected,
      accepted: true,
    };
  }

  return {
    points: reliablePoints,
    rejected: decision.rejected,
    accepted: false,
  };
}

export function filterReliableGPSPoints(
  points: GPSPoint[],
  activityType?: string | null,
  options?: {
    activityStartedAtMs?: number | null;
    onReject?: (rejection: GPSPointRejection) => void;
  },
): { points: GPSPoint[]; rejected: GPSPointRejection[] } {
  const reliablePoints: GPSPoint[] = [];
  const rejected: GPSPointRejection[] = [];
  const originalIndexByPoint = new Map(
    points.map((point, index) => [point, index] as const),
  );

  for (let index = 0; index < points.length; index += 1) {
    const point = points[index];
    // Mutate the accumulator in place: recovery batches can hold tens of
    // thousands of points, so per-point array copies are unaffordable here.
    const decision = evaluateReliableGPSPointAppend(
      reliablePoints.at(-1) ?? null,
      reliablePoints.at(-2) ?? null,
      point,
      activityType,
      { activityStartedAtMs: options?.activityStartedAtMs ?? null },
    );

    if (decision.op === "append") {
      reliablePoints.push(point);
    } else if (decision.op === "replace-last") {
      reliablePoints.pop();
      reliablePoints.push(point);
    }

    for (const item of decision.rejected) {
      const rejection = {
        index: originalIndexByPoint.get(item.point) ?? index,
        reason: item.reason,
        point: item.point,
      };
      rejected.push(rejection);
      options?.onReject?.(rejection);
    }
  }

  return { points: reliablePoints, rejected };
}

export function deriveSpeedMpsFromPoints(
  prevPoint: GPSPoint,
  nextPoint: GPSPoint,
  minDeltaSeconds = DEFAULT_AUTO_PAUSE_CONFIG.minDeltaSeconds,
  maxSpeedMps = DEFAULT_AUTO_PAUSE_CONFIG.maxSpeedMps,
): number | null {
  if ((prevPoint.segmentId ?? 0) !== (nextPoint.segmentId ?? 0)) return null;
  const t1 = new Date(prevPoint.timestamp).getTime();
  const t2 = new Date(nextPoint.timestamp).getTime();
  if (!Number.isFinite(t1) || !Number.isFinite(t2)) return null;
  const dtSeconds = (t2 - t1) / 1000;
  if (!Number.isFinite(dtSeconds) || dtSeconds < minDeltaSeconds) return null;

  const distanceMeters = haversineDistance(
    prevPoint.latitude,
    prevPoint.longitude,
    nextPoint.latitude,
    nextPoint.longitude,
  );
  if (!Number.isFinite(distanceMeters) || distanceMeters < 0) return null;

  const speedMps = distanceMeters / dtSeconds;
  if (!Number.isFinite(speedMps) || speedMps < 0 || speedMps > maxSpeedMps) {
    return null;
  }

  return speedMps;
}

export function normalizeSpeedMps(
  point: GPSPoint,
  prevPoint: GPSPoint | null,
  config: Pick<AutoPauseDetectorConfig, "maxSpeedMps" | "minDeltaSeconds"> = {
    maxSpeedMps: DEFAULT_AUTO_PAUSE_CONFIG.maxSpeedMps,
    minDeltaSeconds: DEFAULT_AUTO_PAUSE_CONFIG.minDeltaSeconds,
  },
): number | null {
  const rawSpeed = point.speed;
  if (
    rawSpeed != null &&
    Number.isFinite(rawSpeed) &&
    rawSpeed >= 0 &&
    rawSpeed <= config.maxSpeedMps
  ) {
    return rawSpeed;
  }

  if (!prevPoint) return null;
  return deriveSpeedMpsFromPoints(
    prevPoint,
    point,
    config.minDeltaSeconds,
    config.maxSpeedMps,
  );
}

export function updateAutoPauseDetector(params: {
  state: AutoPauseDetectorState;
  config: AutoPauseDetectorConfig;
  activityStartedAtMs: number;
  timestampMs: number;
  speedMps: number | null;
  accuracyMeters?: number | null;
}): { nextState: AutoPauseDetectorState; decision: AutoPauseDecision } {
  const { state, config } = params;

  const timestampMs = params.timestampMs;
  if (!Number.isFinite(timestampMs)) {
    return { nextState: state, decision: "none" };
  }

  const isWarmup =
    Number.isFinite(params.activityStartedAtMs) &&
    timestampMs - params.activityStartedAtMs < config.warmupMs;

  const usableAccuracy = isAccuracyUsable(
    params.accuracyMeters,
    config.maxAccuracyMeters,
  );
  const speedMps =
    params.speedMps != null && Number.isFinite(params.speedMps)
      ? params.speedMps
      : null;

  const isUsableSample =
    usableAccuracy &&
    speedMps != null &&
    speedMps >= 0 &&
    speedMps <= config.maxSpeedMps;

  const nextBase: AutoPauseDetectorState = {
    ...state,
    lastTimestampMs: timestampMs,
    lastSpeedMps: speedMps,
    samples: state.samples + (isUsableSample ? 1 : 0),
  };

  if (!isUsableSample) {
    return { nextState: nextBase, decision: "none" };
  }

  // Warmup: ignore auto-pause triggers (avoids immediate pauses at start).
  if (isWarmup && nextBase.status === "moving") {
    return {
      nextState: {
        ...nextBase,
        belowThresholdSinceMs: null,
        aboveResumeSinceMs: null,
      },
      decision: "none",
    };
  }

  const pauseThreshold = config.pauseThresholdMps;
  const resumeThreshold = Math.max(
    config.resumeThresholdMps,
    config.pauseThresholdMps + 0.3,
  );

  if (nextBase.status === "moving") {
    if (nextBase.samples < config.minSamples) {
      return { nextState: nextBase, decision: "none" };
    }

    if (speedMps < pauseThreshold) {
      const belowSince = nextBase.belowThresholdSinceMs ?? timestampMs;
      const shouldPause = timestampMs - belowSince >= config.pauseAfterMs;
      const nextState: AutoPauseDetectorState = {
        ...nextBase,
        belowThresholdSinceMs: belowSince,
        aboveResumeSinceMs: null,
        ...(shouldPause
          ? { status: "paused", belowThresholdSinceMs: null }
          : {}),
      };
      return { nextState, decision: shouldPause ? "pause" : "none" };
    }

    return {
      nextState: {
        ...nextBase,
        belowThresholdSinceMs: null,
        aboveResumeSinceMs: null,
      },
      decision: "none",
    };
  }

  // status === "paused"
  if (speedMps >= resumeThreshold) {
    const aboveSince = nextBase.aboveResumeSinceMs ?? timestampMs;
    const shouldResume = timestampMs - aboveSince >= config.resumeAfterMs;
    const nextState: AutoPauseDetectorState = {
      ...nextBase,
      aboveResumeSinceMs: aboveSince,
      belowThresholdSinceMs: null,
      ...(shouldResume ? { status: "moving", aboveResumeSinceMs: null } : {}),
    };
    return { nextState, decision: shouldResume ? "resume" : "none" };
  }

  return {
    nextState: {
      ...nextBase,
      aboveResumeSinceMs: null,
      belowThresholdSinceMs: null,
    },
    decision: "none",
  };
}

// ──────────────────────────────────────────────────────────────────────────────
// DURATION FORMATTING
// ──────────────────────────────────────────────────────────────────────────────

/**
 * Format duration as HH:MM:SS or MM:SS
 */
export function formatDuration(seconds: number): string {
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  const secs = Math.floor(seconds % 60);

  if (hours > 0) {
    return `${hours}:${minutes.toString().padStart(2, "0")}:${secs.toString().padStart(2, "0")}`;
  }
  return `${minutes}:${secs.toString().padStart(2, "0")}`;
}

/**
 * Format distance as km or miles
 */
export function formatDistance(meters: number, useMetric = true): string {
  if (useMetric) {
    if (meters < 1000) {
      return `${Math.round(meters)}m`;
    }
    return `${(meters / 1000).toFixed(2)} km`;
  }
  const miles = meters / 1609.344;
  return `${miles.toFixed(2)} mi`;
}

// ──────────────────────────────────────────────────────────────────────────────
// CALCULATE FULL STATS
// ──────────────────────────────────────────────────────────────────────────────

/**
 * Calculate all stats from GPS points
 */
export function calculateActivityStats(
  points: GPSPoint[],
  pausedSeconds = 0,
  options: ActivityStatsOptions = {},
): CalculatedStats {
  // Distance should use the recorded route, not averaged coordinates.
  // Smoothing can visually reduce jitter, but it cuts corners and undercounts
  // real distance on winding routes.
  const distanceMeters = calculateTotalDistance(points);

  // Activity duration is a wall-clock property. Point-span duration silently
  // truncates a run whenever the WebView stops receiving background callbacks.
  const pointStartTime = toTimestampMs(points[0]?.timestamp ?? "");
  const pointEndTime = toTimestampMs(points.at(-1)?.timestamp ?? "");
  const requestedStartTime = toStatsTimestampMs(options.startedAt);
  const requestedEndTime = toStatsTimestampMs(options.completedAt);
  const startTime = requestedStartTime ?? pointStartTime;
  const endTime = requestedEndTime ?? pointEndTime;
  const durationSeconds =
    startTime != null && endTime != null && endTime >= startTime
      ? (endTime - startTime) / 1000
      : 0;
  const { recordingGapSeconds, maxRecordingGapSeconds, edgeGapSeconds } =
    calculateRecordingGapSummary(points, pausedSeconds, {
      startedAtMs: startTime,
      completedAtMs: endTime,
      thresholdSeconds: options.recordingGapThresholdSeconds ?? 30,
    });
  // Edge gaps (before the first fix / after the last fix) carry no recorded
  // distance, so counting them as moving time would dilute pace the same way
  // the old point-span duration inflated it. Interior gaps keep their time
  // because the straight-line distance across the hole is still summed.
  const movingTimeSeconds = Math.max(
    0,
    durationSeconds - pausedSeconds - edgeGapSeconds,
  );

  // Calculate elevation
  const elevationGainMeters = calculateElevationGain(points);
  const elevationLossMeters = calculateElevationLoss(points);

  // Calculate pace and speed
  const avgSpeedMps = calculateSpeed(distanceMeters, movingTimeSeconds);
  const avgPaceSecondsPerKm = calculatePace(distanceMeters, movingTimeSeconds);

  // Find max speed
  const reportedMaxSpeedMps = Math.max(
    ...points
      .filter(
        (p): p is GPSPoint & { speed: number } =>
          p.speed !== undefined && p.speed !== null,
      )
      .map((p) => p.speed),
    0,
  );
  let derivedMaxSpeedMps = 0;
  for (let index = 1; index < points.length; index += 1) {
    if ((points[index - 1].segmentId ?? 0) !== (points[index].segmentId ?? 0))
      continue;
    const previousTimestampMs = toTimestampMs(points[index - 1].timestamp);
    const currentTimestampMs = toTimestampMs(points[index].timestamp);
    if (previousTimestampMs == null || currentTimestampMs == null) continue;
    const deltaSeconds = (currentTimestampMs - previousTimestampMs) / 1000;
    if (deltaSeconds <= 0) continue;
    const segmentSpeedMps =
      haversineDistance(
        points[index - 1].latitude,
        points[index - 1].longitude,
        points[index].latitude,
        points[index].longitude,
      ) / deltaSeconds;
    if (Number.isFinite(segmentSpeedMps) && segmentSpeedMps >= 0) {
      derivedMaxSpeedMps = Math.max(derivedMaxSpeedMps, segmentSpeedMps);
    }
  }
  const maxSpeedMps = Math.max(reportedMaxSpeedMps, derivedMaxSpeedMps);

  return {
    distanceMeters,
    durationSeconds,
    movingTimeSeconds,
    elevationGainMeters,
    elevationLossMeters,
    avgPaceSecondsPerKm,
    avgSpeedMps,
    maxSpeedMps,
    recordingGapSeconds,
    maxRecordingGapSeconds,
  };
}

function toStatsTimestampMs(
  value: string | number | Date | null | undefined,
): number | null {
  if (value == null) return null;
  const milliseconds =
    value instanceof Date
      ? value.getTime()
      : typeof value === "number"
        ? value
        : new Date(value).getTime();
  return Number.isFinite(milliseconds) ? milliseconds : null;
}

function calculateRecordingGapSummary(
  points: GPSPoint[],
  pausedSeconds: number,
  options: {
    startedAtMs: number | null;
    completedAtMs: number | null;
    thresholdSeconds: number;
  },
): {
  recordingGapSeconds: number;
  maxRecordingGapSeconds: number;
  edgeGapSeconds: number;
} {
  if (options.startedAtMs == null || options.completedAtMs == null) {
    return {
      recordingGapSeconds: 0,
      maxRecordingGapSeconds: 0,
      edgeGapSeconds: 0,
    };
  }
  const startedAtMs = options.startedAtMs;
  const completedAtMs = options.completedAtMs;

  const pointTimes = points
    .map((point) => toTimestampMs(point.timestamp))
    .filter(
      (timestamp): timestamp is number =>
        timestamp != null &&
        timestamp >= startedAtMs &&
        timestamp <= completedAtMs,
    );
  const boundaries = [startedAtMs, ...pointTimes, completedAtMs];
  const thresholdSeconds = Math.max(1, options.thresholdSeconds);
  let rawGapSeconds = 0;
  let largestRawGapSeconds = 0;
  let rawEdgeGapSeconds = 0;

  for (let index = 1; index < boundaries.length; index += 1) {
    const gapSeconds = Math.max(
      0,
      (boundaries[index] - boundaries[index - 1]) / 1000,
    );
    const excessSeconds = Math.max(0, gapSeconds - thresholdSeconds);
    rawGapSeconds += excessSeconds;
    largestRawGapSeconds = Math.max(largestRawGapSeconds, excessSeconds);
    // Leading (start → first point) and trailing (last point → completion)
    // gaps have no recorded distance at all, unlike interior gaps whose
    // straight-line distance is still summed across the hole.
    if (index === 1 || index === boundaries.length - 1) {
      rawEdgeGapSeconds += excessSeconds;
    }
  }

  const safePausedSeconds = Number.isFinite(pausedSeconds)
    ? Math.max(0, pausedSeconds)
    : 0;
  return {
    recordingGapSeconds: Math.max(0, rawGapSeconds - safePausedSeconds),
    maxRecordingGapSeconds: Math.max(
      0,
      largestRawGapSeconds - safePausedSeconds,
    ),
    edgeGapSeconds: Math.max(0, rawEdgeGapSeconds - safePausedSeconds),
  };
}

export function getActivityQualityFlags(
  stats: CalculatedStats,
  activityType?: string | null,
): ActivityQualityFlag[] {
  const flags: ActivityQualityFlag[] = [];
  if (stats.recordingGapSeconds > 0) {
    flags.push("recording-gap");
  }
  if (stats.avgSpeedMps > stats.maxSpeedMps + 0.25) {
    flags.push("average-speed-exceeds-maximum");
  }
  if (stats.avgSpeedMps > getGPSQualityRules(activityType).maxSegmentSpeedMps) {
    flags.push("activity-speed-impossible");
  }
  return flags;
}
